import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Plus,
  Search,
  Loader2,
  RefreshCw,
  X,
  Mail,
  Phone,
  Building2,
  User as UserIcon,
  Trash2,
  ArrowRight,
  LayoutGrid,
  List,
  Send,
  PhoneCall,
  Users as UsersIcon,
  MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

export const STAGES = ["new", "contacted", "qualified", "proposal", "won", "lost"];

export const STAGE_META = {
  new: { label: "New", color: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
  contacted: { label: "Contacted", color: "bg-blue-500/15 text-blue-300 border-blue-500/40" },
  qualified: { label: "Qualified", color: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40" },
  proposal: { label: "Proposal", color: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40" },
  won: { label: "Won", color: "bg-green-500/15 text-green-300 border-green-500/40" },
  lost: { label: "Lost", color: "bg-red-500/15 text-red-300 border-red-500/40" },
};

export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export const StagePill = ({ stage }) => {
  const meta = STAGE_META[stage] || STAGE_META.new;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border font-mono text-[10px] tracking-[0.14em] uppercase ${meta.color}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {meta.label}
    </span>
  );
};

const emptyLead = {
  name: "",
  email: "",
  phone: "",
  company: "",
  service: "",
  notes: "",
  stage: "new",
  value: "",
  currency: "USD",
  product_id: "",
};

export const LEAD_PRODUCTS = [
  { id: "shield", name: "Sriman Shield" },
  { id: "d", name: "Sriman D" },
];

const inputClass =
  "w-full bg-[#040812]/70 border border-white/10 focus:border-red-500/60 focus:outline-none text-white placeholder-slate-500 px-4 py-3 text-sm rounded-sm transition-colors";

export const LeadForm = ({
  initial,
  onSubmit,
  onCancel,
  showStage = true,
  submitLabel = "Save lead",
  loading = false,
}) => {
  const [values, setValues] = useState({ ...emptyLead, ...(initial || {}) });
  const set = (k) => (e) => setValues((v) => ({ ...v, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!values.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    const payload = {
      ...values,
      value: values.value ? parseFloat(values.value) : null,
    };
    if (!payload.email) delete payload.email;
    if (!payload.product_id) delete payload.product_id;
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="overline mb-1.5 block">Name *</label>
          <input
            data-testid="lead-form-name"
            value={values.name}
            onChange={set("name")}
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className="overline mb-1.5 block">Email</label>
          <input
            data-testid="lead-form-email"
            type="email"
            value={values.email}
            onChange={set("email")}
            className={inputClass}
          />
        </div>
        <div>
          <label className="overline mb-1.5 block">Phone</label>
          <input value={values.phone} onChange={set("phone")} className={inputClass} />
        </div>
        <div>
          <label className="overline mb-1.5 block">Company</label>
          <input value={values.company} onChange={set("company")} className={inputClass} />
        </div>
        <div>
          <label className="overline mb-1.5 block">Service</label>
          <input value={values.service} onChange={set("service")} className={inputClass} />
        </div>
        <div>
          <label className="overline mb-1.5 block">Product interest</label>
          <select
            data-testid="lead-form-product"
            value={values.product_id || ""}
            onChange={set("product_id")}
            className={inputClass}
          >
            <option value="">— None —</option>
            {LEAD_PRODUCTS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <div className="mt-1.5 text-[10px] font-mono text-slate-500 tracking-widest uppercase">
            Selecting a product auto-sends a branded intro email to this lead
          </div>
        </div>
        {showStage && (
          <div>
            <label className="overline mb-1.5 block">Stage</label>
            <select value={values.stage} onChange={set("stage")} className={inputClass}>
              {STAGES.map((s) => (
                <option key={s} value={s}>
                  {STAGE_META[s].label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="overline mb-1.5 block">Deal value</label>
          <input
            type="number"
            step="0.01"
            value={values.value}
            onChange={set("value")}
            className={inputClass}
          />
        </div>
        <div>
          <label className="overline mb-1.5 block">Currency</label>
          <input value={values.currency} onChange={set("currency")} className={inputClass} />
        </div>
      </div>
      <div>
        <label className="overline mb-1.5 block">Notes</label>
        <textarea rows={4} value={values.notes} onChange={set("notes")} className={inputClass} />
      </div>
      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={loading}
          data-testid="lead-form-submit"
          className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-70 text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
          {submitLabel}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-slate-400 hover:text-white text-sm"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

/**
 * LeadsPanel — shared UI used by both admin (Leads tab) and vendor CRM.
 * `variant` = "admin" | "vendor"
 * Admin can assign leads via `vendors` list.
 */
export default function LeadsPanel({ variant = "vendor", vendors = [] }) {
  const { authHeader, apiBase } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [query, setQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/leads`, { headers: authHeader() });
      setLeads(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (stageFilter !== "all" && l.stage !== stageFilter) return false;
      if (!q) return true;
      return [l.name, l.email, l.company, l.service, l.notes, l.phone]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [leads, query, stageFilter]);

  const stageCounts = useMemo(() => {
    const c = { total: leads.length };
    for (const s of STAGES) c[s] = 0;
    for (const l of leads) if (c[l.stage] !== undefined) c[l.stage] += 1;
    return c;
  }, [leads]);

  const createLead = async (payload) => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${apiBase}/leads`, payload, { headers: authHeader() });
      setLeads((list) => [data, ...list]);
      toast.success("Lead created.");
      setSelected(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const updateLead = async (id, payload) => {
    setSavingId(id);
    try {
      const { data } = await axios.patch(`${apiBase}/leads/${id}`, payload, {
        headers: authHeader(),
      });
      setLeads((list) => list.map((l) => (l.id === id ? data : l)));
      setSelected(data);
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Update failed");
    } finally {
      setSavingId(null);
    }
  };

  const assignLead = async (id, ownerId) => {
    try {
      const { data } = await axios.post(
        `${apiBase}/admin/leads/${id}/assign`,
        { owner_id: ownerId || null },
        { headers: authHeader() },
      );
      setLeads((list) => list.map((l) => (l.id === id ? data : l)));
      setSelected(data);
      toast.success(ownerId ? "Assigned." : "Unassigned.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Assign failed");
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${apiBase}/leads/${id}`, { headers: authHeader() });
      setLeads((list) => list.filter((l) => l.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Lead deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const [showNewForm, setShowNewForm] = useState(false);
  const [view, setView] = useState("table"); // 'table' | 'board'
  const [activityKind, setActivityKind] = useState("note");
  const [activityContent, setActivityContent] = useState("");
  const [addingActivity, setAddingActivity] = useState(false);

  const addActivity = async (leadId) => {
    if (!activityContent.trim()) return;
    setAddingActivity(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/leads/${leadId}/activities`,
        { kind: activityKind, content: activityContent.trim() },
        { headers: authHeader() },
      );
      // append to selected lead + list
      setSelected((sel) => {
        if (!sel || sel.id !== leadId) return sel;
        return { ...sel, activities: [...(sel.activities || []), data] };
      });
      setLeads((list) =>
        list.map((l) =>
          l.id === leadId ? { ...l, activities: [...(l.activities || []), data] } : l,
        ),
      );
      setActivityContent("");
      toast.success("Activity logged.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Could not add activity",
      );
    } finally {
      setAddingActivity(false);
    }
  };

  // Native HTML5 drag between Kanban lanes
  const onDragStart = (e, lead) => {
    e.dataTransfer.setData("text/lead-id", lead.id);
    e.dataTransfer.setData("text/lead-stage", lead.stage);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDropStage = (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/lead-id");
    const from = e.dataTransfer.getData("text/lead-stage");
    if (!id || from === stage) return;
    const lead = leads.find((l) => l.id === id);
    if (!lead) return;
    updateLead(id, { stage });
  };
  const allowDrop = (e) => e.preventDefault();

  // Emails for the selected lead
  const [leadEmails, setLeadEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [previewEmail, setPreviewEmail] = useState(null);
  useEffect(() => {
    if (!selected) {
      setLeadEmails([]);
      return;
    }
    let alive = true;
    (async () => {
      setLoadingEmails(true);
      try {
        const { data } = await axios.get(
          `${apiBase}/leads/${selected.id}/emails`,
          { headers: authHeader() },
        );
        if (alive) setLeadEmails(data);
      } catch {
        if (alive) setLeadEmails([]);
      } finally {
        if (alive) setLoadingEmails(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selected, apiBase, authHeader]);

  return (
    <div data-testid={`${variant}-leads-panel`}>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <span className="overline">/{variant === "admin" ? "Admin" : "CRM"} · Leads</span>
          <h1 className="mt-4 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
            {variant === "admin" ? "All leads" : "Your pipeline"}
          </h1>
          <p className="mt-3 text-slate-500 text-sm">
            {variant === "admin"
              ? "Every lead from the website, product page and your team — assign, track, close."
              : "Track every conversation from first contact to signed deal."}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={load}
            disabled={loading}
            data-testid={`${variant}-leads-refresh`}
            className="inline-flex items-center gap-2 border border-white/15 hover:border-white text-white text-sm px-4 py-2.5 rounded-sm transition-colors disabled:opacity-60"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={() => setShowNewForm(true)}
            data-testid={`${variant}-leads-new`}
            className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-5 py-2.5 rounded-sm transition-colors"
          >
            <Plus size={14} />
            New lead
          </button>
        </div>
      </div>

      {/* View toggle */}
      <div className="mt-6 inline-flex items-center gap-1 bg-[#0B132B]/60 border border-white/10 p-1 rounded-sm">
        <button
          onClick={() => setView("table")}
          data-testid={`${variant}-view-table`}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-mono tracking-widest uppercase transition-colors ${
            view === "table"
              ? "bg-red-500 text-white"
              : "text-slate-400 hover:text-white"
          }`}
        >
          <List size={12} /> Table
        </button>
        <button
          onClick={() => setView("board")}
          data-testid={`${variant}-view-board`}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-mono tracking-widest uppercase transition-colors ${
            view === "board"
              ? "bg-red-500 text-white"
              : "text-slate-400 hover:text-white"
          }`}
        >
          <LayoutGrid size={12} /> Board
        </button>
      </div>

      {/* Pipeline stats */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-7 gap-3">
        <button
          onClick={() => setStageFilter("all")}
          className={`text-left bg-[#0B132B]/50 border p-4 transition-colors ${
            stageFilter === "all" ? "border-red-500" : "border-white/10 hover:border-white/25"
          }`}
        >
          <div className="font-display text-white text-2xl">{stageCounts.total}</div>
          <div className="mt-1 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
            Total
          </div>
        </button>
        {STAGES.map((s) => (
          <button
            key={s}
            onClick={() => setStageFilter(s)}
            data-testid={`${variant}-stage-${s}`}
            className={`text-left bg-[#0B132B]/50 border p-4 transition-colors ${
              stageFilter === s ? "border-red-500" : "border-white/10 hover:border-white/25"
            }`}
          >
            <div className="font-display text-white text-2xl">{stageCounts[s]}</div>
            <div className="mt-1 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase">
              {STAGE_META[s].label}
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mt-8 relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          data-testid={`${variant}-leads-search`}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by name, email, company or notes…"
          className="w-full bg-[#0B132B]/60 border border-white/10 focus:border-red-500/60 focus:outline-none text-white placeholder-slate-500 pl-11 pr-4 py-3 text-sm rounded-sm transition-colors"
        />
      </div>

      {/* Table view */}
      {view === "table" && (
      <div className="mt-6 bg-[#0B132B]/40 border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left">
                {["Updated", "Lead", "Company", "Service", "Source", "Stage"].concat(
                  variant === "admin" ? ["Owner"] : [],
                ).map((h) => (
                  <th
                    key={h}
                    className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.22em] uppercase"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={variant === "admin" ? 7 : 6} className="px-6 py-16 text-center">
                    <Loader2 size={20} className="inline animate-spin text-slate-500" />
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={variant === "admin" ? 7 : 6} className="px-6 py-16 text-center">
                    <div className="text-slate-500 text-sm">
                      {leads.length === 0
                        ? "No leads yet."
                        : "No leads match your filter."}
                    </div>
                  </td>
                </tr>
              )}
              {!loading &&
                filtered.map((l) => {
                  const owner = vendors.find((v) => v.id === l.owner_id);
                  return (
                    <tr
                      key={l.id}
                      data-testid={`${variant}-lead-row-${l.id}`}
                      onClick={() => setSelected(l)}
                      className="border-b border-white/5 last:border-0 hover:bg-white/[0.02] cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 text-slate-400 font-mono text-xs whitespace-nowrap">
                        {formatDate(l.updated_at || l.created_at)}
                      </td>
                      <td className="px-6 py-4 text-white">
                        <div>{l.name}</div>
                        <div className="text-slate-500 text-xs">{l.email || ""}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-300">{l.company || "—"}</td>
                      <td className="px-6 py-4 text-slate-400">{l.service || "—"}</td>
                      <td className="px-6 py-4 font-mono text-[10px] text-slate-500 tracking-[0.14em] uppercase">
                        {l.source}
                      </td>
                      <td className="px-6 py-4">
                        <StagePill stage={l.stage} />
                      </td>
                      {variant === "admin" && (
                        <td className="px-6 py-4 text-slate-300 text-xs">
                          {owner ? owner.name : <span className="text-slate-500">Unassigned</span>}
                        </td>
                      )}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Board view */}
      {view === "board" && (
        <div
          data-testid={`${variant}-board`}
          className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
        >
          {STAGES.map((s) => {
            const stageLeads = filtered.filter((l) => l.stage === s);
            return (
              <div
                key={s}
                onDragOver={allowDrop}
                onDrop={(e) => onDropStage(e, s)}
                data-testid={`${variant}-board-lane-${s}`}
                className="bg-[#0B132B]/40 border border-white/10 min-h-[240px] flex flex-col"
              >
                <div className="p-3 border-b border-white/5 flex items-center justify-between">
                  <StagePill stage={s} />
                  <span className="font-mono text-xs text-slate-500">{stageLeads.length}</span>
                </div>
                <div className="p-2 flex-1 space-y-2">
                  {stageLeads.map((l) => (
                    <div
                      key={l.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, l)}
                      onClick={() => setSelected(l)}
                      data-testid={`${variant}-board-card-${l.id}`}
                      className="cursor-grab active:cursor-grabbing bg-[#040812]/70 border border-white/10 hover:border-red-500/50 p-3 transition-colors"
                    >
                      <div className="text-white text-sm">{l.name}</div>
                      {l.company && (
                        <div className="text-slate-500 text-xs mt-0.5">{l.company}</div>
                      )}
                      {l.service && (
                        <div className="font-mono text-[10px] text-slate-500 tracking-widest mt-2 uppercase">
                          {l.service}
                        </div>
                      )}
                    </div>
                  ))}
                  {stageLeads.length === 0 && (
                    <div className="text-slate-600 text-xs font-mono py-8 text-center">
                      Drop here
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New lead modal */}
      {showNewForm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowNewForm(false)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-none" />
          <div
            className="relative w-full max-w-2xl bg-[#0B132B] border border-white/10 p-8 max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline">New lead</div>
                <h2 className="font-display text-white text-2xl tracking-tight mt-2">Add lead</h2>
              </div>
              <button onClick={() => setShowNewForm(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <LeadForm
              onSubmit={async (p) => {
                await createLead(p);
                setShowNewForm(false);
              }}
              onCancel={() => setShowNewForm(false)}
              loading={creating}
              submitLabel="Create lead"
            />
          </div>
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div
          data-testid={`${variant}-lead-drawer`}
          className="fixed inset-0 z-50 flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-none" />
          <div
            className="relative w-full max-w-xl bg-[#0B132B] border-l border-white/10 h-full overflow-y-auto p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Lead detail</div>
                <h2 className="mt-3 font-display text-white text-2xl tracking-tight">
                  {selected.name}
                </h2>
                <div className="mt-2 flex items-center gap-2">
                  <StagePill stage={selected.stage} />
                  <span className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">
                    · {selected.source}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                data-testid={`${variant}-lead-drawer-close`}
                className="text-slate-400 hover:text-white text-sm"
              >
                Close
              </button>
            </div>

            <div className="mt-6 space-y-3 text-sm">
              {selected.email && (
                <a
                  href={`mailto:${selected.email}`}
                  className="flex items-center gap-2 text-slate-300 hover:text-red-400"
                >
                  <Mail size={14} /> {selected.email}
                </a>
              )}
              {selected.phone && (
                <a
                  href={`tel:${selected.phone}`}
                  className="flex items-center gap-2 text-slate-300 hover:text-red-400"
                >
                  <Phone size={14} /> {selected.phone}
                </a>
              )}
              {selected.company && (
                <div className="flex items-center gap-2 text-slate-300">
                  <Building2 size={14} /> {selected.company}
                </div>
              )}
            </div>

            {variant === "admin" && (
              <div className="mt-8 bg-[#040812]/60 border border-white/10 p-4 rounded-sm">
                <div className="overline mb-2 flex items-center gap-2">
                  <UserIcon size={12} /> Owner
                </div>
                <select
                  data-testid="admin-lead-assign-select"
                  value={selected.owner_id || ""}
                  onChange={(e) => assignLead(selected.id, e.target.value || null)}
                  className={inputClass}
                >
                  <option value="">Unassigned</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>
                      {`${v.name} (${v.email})${v.active === false ? " · inactive" : ""}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="mt-8">
              <LeadForm
                initial={{
                  ...selected,
                  value: selected.value ?? "",
                  currency: selected.currency ?? "",
                }}
                onSubmit={(p) => updateLead(selected.id, p)}
                loading={savingId === selected.id}
                submitLabel="Save changes"
              />
            </div>

            {/* Emails sent to this lead */}
            <div className="mt-10 pt-6 border-t border-white/5">
              <div className="flex items-center justify-between">
                <div className="overline">Emails sent</div>
                <div className="font-mono text-[10px] text-slate-500 tracking-widest uppercase">
                  {loadingEmails ? "loading…" : `${leadEmails.length} total`}
                </div>
              </div>

              {!loadingEmails && leadEmails.length === 0 && (
                <div className="mt-3 text-slate-500 text-xs">No emails sent to this lead yet.</div>
              )}

              <ul className="mt-3 space-y-2" data-testid="lead-emails-list">
                {leadEmails.map((em, idx) => (
                  <li
                    key={idx}
                    data-testid={`lead-email-item-${idx}`}
                    className="bg-[#040812]/60 border border-white/10 p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-white text-sm truncate">{em.subject || "(no subject)"}</div>
                        <div className="mt-1 text-[10px] font-mono text-slate-500 tracking-widest uppercase">
                          {formatDate(em.created_at)} · to {em.to} ·{" "}
                          <span
                            className={
                              em.status === "sent"
                                ? "text-green-400"
                                : em.status === "failed"
                                ? "text-red-400"
                                : "text-yellow-300"
                            }
                          >
                            {em.status}
                          </span>
                          {em.tag ? <> · {em.tag}</> : null}
                        </div>
                      </div>
                      {em.html ? (
                        <button
                          type="button"
                          onClick={() => setPreviewEmail(em)}
                          data-testid={`lead-email-preview-${idx}`}
                          className="shrink-0 text-red-400 hover:text-red-300 text-xs underline-offset-4 hover:underline"
                        >
                          Preview
                        </button>
                      ) : (
                        <span className="shrink-0 text-slate-600 text-xs">no preview</span>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10 pt-6 border-t border-white/5">
              <div className="overline mb-3">Activity log</div>

              {/* Add activity */}
              <div className="bg-[#040812]/70 border border-white/10 p-3 rounded-sm">
                <div className="flex items-center gap-1 mb-2">
                  {[
                    { k: "note", icon: MessageSquare, label: "Note" },
                    { k: "call", icon: PhoneCall, label: "Called" },
                    { k: "email", icon: Send, label: "Emailed" },
                    { k: "meeting", icon: UsersIcon, label: "Meeting" },
                  ].map(({ k, icon: Icon, label }) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setActivityKind(k)}
                      data-testid={`activity-kind-${k}`}
                      className={`inline-flex items-center gap-1.5 px-2 py-1.5 rounded-sm text-[10px] font-mono tracking-widest uppercase transition-colors ${
                        activityKind === k
                          ? "bg-red-500 text-white"
                          : "text-slate-400 hover:text-white"
                      }`}
                    >
                      <Icon size={11} /> {label}
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="activity-input"
                  rows={2}
                  value={activityContent}
                  onChange={(e) => setActivityContent(e.target.value)}
                  placeholder={
                    activityKind === "note"
                      ? "Add a note…"
                      : `Log a ${activityKind}…`
                  }
                  className="w-full bg-transparent text-white text-sm focus:outline-none placeholder-slate-500"
                />
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => addActivity(selected.id)}
                    disabled={addingActivity || !activityContent.trim()}
                    data-testid="activity-submit"
                    className="inline-flex items-center gap-1.5 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white text-xs font-medium px-3 py-1.5 rounded-sm"
                  >
                    {addingActivity ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Plus size={12} />
                    )}
                    Log
                  </button>
                </div>
              </div>

              {/* Combined timeline */}
              <ul className="mt-4 space-y-3">
                {[
                  ...((selected.activities || []).map((a) => ({ ...a, _type: "activity" }))),
                  ...((selected.stage_history || []).map((h) => ({
                    ...h,
                    _type: "stage",
                    id: `stage-${h.changed_at}`,
                    created_at: h.changed_at,
                    kind: "stage_change",
                    content: `Stage → ${h.stage}`,
                  }))),
                ]
                  .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))
                  .slice(0, 20)
                  .map((entry) => (
                    <li
                      key={entry.id}
                      className="border-l-2 border-white/10 pl-3 py-1"
                    >
                      <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500 tracking-widest uppercase">
                        {entry.kind || entry._type}
                        <span>·</span>
                        <span>{formatDate(entry.created_at)}</span>
                        {entry.created_by_name && (
                          <>
                            <span>·</span>
                            <span>{entry.created_by_name}</span>
                          </>
                        )}
                      </div>
                      <div className="text-slate-200 text-sm mt-1 whitespace-pre-wrap">
                        {entry.content}
                      </div>
                    </li>
                  ))}
                {(selected.activities || []).length === 0 &&
                  (selected.stage_history || []).length === 0 && (
                    <li className="text-slate-500 text-xs">No activity yet.</li>
                  )}
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-white/5 flex justify-end">
              <button
                onClick={() => deleteLead(selected.id)}
                className="inline-flex items-center gap-2 text-slate-500 hover:text-red-500 text-xs"
              >
                <Trash2 size={12} /> Delete lead
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email preview modal */}
      {previewEmail && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          onClick={() => setPreviewEmail(null)}
          data-testid="lead-email-preview-modal"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm pointer-events-none" />
          <div
            className="relative w-full max-w-2xl bg-[#0B132B] border border-white/10 max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-white/10">
              <div className="min-w-0">
                <div className="overline">Email preview</div>
                <div className="mt-2 text-white text-sm truncate">{previewEmail.subject}</div>
                <div className="mt-1 font-mono text-[10px] text-slate-500 tracking-widest uppercase">
                  {formatDate(previewEmail.created_at)} · to {previewEmail.to}
                </div>
              </div>
              <button
                onClick={() => setPreviewEmail(null)}
                className="text-slate-400 hover:text-white"
                data-testid="lead-email-preview-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto bg-white">
              <iframe
                title="email-preview"
                srcDoc={previewEmail.html || ""}
                className="w-full min-h-[70vh] bg-white"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
